import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { useI18n, LANGS } from "@/i18n";
import { langPath, switchLangPath, splitHrefHash, normalizePath } from "@/lib/paths";

export default function Header() {
  const { t, language } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const nav = [
    { key: "home", to: langPath(language, "/"), label: t("nav.home") },
    { key: "how", to: langPath(language, "/#how"), label: t("nav.howItWorks") },
    { key: "projects", to: langPath(language, "/#projects"), label: t("nav.projects") },
    { key: "about", to: langPath(language, "/#about"), label: t("nav.about") },
    { key: "contact", to: langPath(language, "/#contact"), label: t("nav.contact") },
  ];

  const handleLangSwitch = (target) => {
    if (target === language) return;
    navigate(switchLangPath(location.pathname, target) + location.hash, { replace: false });
  };

  return (
    <header className="header sticky top-0 z-40 bg-[color:var(--rv-bg)]/85 backdrop-blur border-b border-[color:var(--rv-line)]">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to={langPath(language, "/")} className="flex items-center gap-3 group">
            <Logo className="h-9 w-9 md:h-10 md:w-10 rounded-lg shadow-sm" />
            <div className="flex flex-col leading-none">
              <span className="font-serif text-[color:var(--rv-ink)] text-lg md:text-xl tracking-tight">{t("nav.brand")}</span>
              <span className="text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-[color:var(--rv-muted)]">{t("nav.brandTag")}</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {nav.map((item) => (
              <a
                key={item.key}
                href={item.to}
                onClick={(e) => {
                  if (item.to.includes("#")) {
                    const { path: pathPart, hash: hashPart } = splitHrefHash(item.to);
                    const homePath = langPath(language, "/");
                    if (pathPart === normalizePath(location.pathname) || pathPart === homePath) {
                      e.preventDefault();
                      const el = document.getElementById(hashPart);
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                      else navigate(item.to);
                    }
                  }
                }}
                className="text-sm text-[color:var(--rv-ink)]/80 hover:text-[color:var(--rv-ink)] transition-colors"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <div className="flex items-center rounded-full border border-[color:var(--rv-line)] p-0.5" role="group" aria-label={t("nav.langSwitch")}>
              {LANGS.map((lg) => (
                <button
                  key={lg}
                  onClick={() => handleLangSwitch(lg)}
                  className={`px-3 py-1 text-xs uppercase tracking-widest rounded-full transition-colors ${
                    language === lg
                      ? "bg-[color:var(--rv-ink)] text-[color:var(--rv-bg)]"
                      : "text-[color:var(--rv-ink)]/70 hover:text-[color:var(--rv-ink)]"
                  }`}
                  aria-pressed={language === lg}
                >
                  {lg}
                </button>
              ))}
            </div>
            <a
              href={langPath(language, "/#contact")}
              onClick={(e) => {
                const { path: pathPart, hash: hashPart } = splitHrefHash(langPath(language, "/#contact"));
                const homePath = langPath(language, "/");
                if (pathPart === normalizePath(location.pathname) || pathPart === homePath) {
                  e.preventDefault();
                  const el = document.getElementById(hashPart);
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  else navigate(langPath(language, "/#contact"));
                } else {
                  // On a different page — navigate with hash so ScrollToTop skips
                  e.preventDefault();
                  navigate(langPath(language, "/") + "#contact");
                  setTimeout(() => {
                    const el = document.getElementById("contact");
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 250);
                }
              }}
              className="inline-flex items-center rounded-full bg-[color:var(--rv-accent)] text-[color:var(--rv-ink)] px-4 py-2 text-sm font-medium hover:bg-[color:var(--rv-accent-hover)] transition-colors"
            >
              {t("home.hero.primaryCta")}
            </a>
          </div>

          <button
            className="md:hidden inline-flex items-center justify-center h-11 w-11 rounded-lg text-[color:var(--rv-ink)]"
            aria-label={open ? t("nav.menuClose") : t("nav.menuOpen")}
            onClick={() => setOpen((v) => !v)}
            data-testid="header-hamburger"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden fixed inset-x-0 top-16 bottom-0 bg-[color:var(--rv-bg)] border-t border-[color:var(--rv-line)] z-40 overflow-y-auto">
          <div className="px-5 py-6 space-y-1">
            {nav.map((item) => (
              <a
                key={item.key}
                href={item.to}
                className="block px-3 py-3 text-lg text-[color:var(--rv-ink)] border-b border-[color:var(--rv-line)]"
                onClick={(e) => {
                  if (item.to.includes("#")) {
                    const { path: pathPart, hash: hashPart } = splitHrefHash(item.to);
                    const homePath = langPath(language, "/");
                    if (pathPart === normalizePath(location.pathname) || pathPart === homePath) {
                      e.preventDefault();
                      setOpen(false);
                      setTimeout(() => {
                        const el = document.getElementById(hashPart);
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }, 50);
                    }
                  }
                }}
              >
                {item.label}
              </a>
            ))}
            <div className="pt-6">
              <div className="text-[11px] uppercase tracking-widest text-[color:var(--rv-muted)] mb-2">
                {t("nav.langSwitch")}
              </div>
              <div className="flex items-center gap-2">
                {LANGS.map((lg) => (
                  <button
                    key={lg}
                    onClick={() => handleLangSwitch(lg)}
                    className={`px-4 py-2 text-sm uppercase tracking-widest rounded-full border ${
                      language === lg
                        ? "bg-[color:var(--rv-ink)] text-[color:var(--rv-bg)] border-[color:var(--rv-ink)]"
                        : "border-[color:var(--rv-line)] text-[color:var(--rv-ink)]"
                    }`}
                  >
                    {lg}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
